import { useMemo } from "react";
import type { CompetencyDimension } from "@/hooks/use-waypoint-sessions";

const SCALE = 5;
const RINGS = 5;

function scoreColor(score: number): string {
  if (score >= 4.0) return "#2EA86A";
  if (score >= 3.0) return "#407EC1";
  return "#D8443C";
}

// ── SVG radar chart ───────────────────────────────────────────────────────────

function polarToCart(cx: number, cy: number, r: number, angleDeg: number) {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function polyPoints(pts: { x: number; y: number }[]): string {
  return pts.map((p) => `${p.x},${p.y}`).join(" ");
}

type RadarProps = {
  dimensions: CompetencyDimension[];
  size?: number;
};

function RadarChart({ dimensions, size = 200 }: RadarProps) {
  const cx = size / 2;
  const cy = size / 2;
  const outerR = size * 0.38;
  const n = dimensions.length;

  const axes = useMemo(
    () =>
      dimensions.map((d, i) => ({
        ...d,
        angle: (360 / n) * i,
        outer: polarToCart(cx, cy, outerR, (360 / n) * i),
        dataPoint: polarToCart(cx, cy, (d.score / SCALE) * outerR, (360 / n) * i),
      })),
    [dimensions, n, cx, cy, outerR],
  );

  // Concentric background rings
  const rings = Array.from({ length: RINGS }, (_, i) => {
    const r = (outerR * (i + 1)) / RINGS;
    const pts = Array.from({ length: n }, (__, j) => polarToCart(cx, cy, r, (360 / n) * j));
    return { r, pts };
  });

  const dataPath = `M ${axes.map((a) => `${a.dataPoint.x} ${a.dataPoint.y}`).join(" L ")} Z`;

  const labelR = outerR + 18;

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      style={{ width: "100%", height: "auto", display: "block" }}
      aria-hidden="true"
    >
      {/* Background rings */}
      {rings.map((ring) => (
        <polygon
          key={ring.r}
          points={polyPoints(ring.pts)}
          fill="none"
          stroke="#e2e8f0"
          strokeWidth={0.8}
        />
      ))}

      {/* Axis spokes */}
      {axes.map((a) => (
        <line
          key={`spoke-${a.slug}`}
          x1={cx}
          y1={cy}
          x2={a.outer.x}
          y2={a.outer.y}
          stroke="#e2e8f0"
          strokeWidth={0.8}
        />
      ))}

      {/* Data polygon */}
      <path d={dataPath} fill="#407EC1" fillOpacity={0.18} stroke="#407EC1" strokeWidth={1.5} />

      {/* Data dots */}
      {axes.map((a) => (
        <circle key={`dot-${a.slug}`} cx={a.dataPoint.x} cy={a.dataPoint.y} r={3} fill="#407EC1" />
      ))}

      {/* Axis labels */}
      {axes.map((a) => {
        const lp = polarToCart(cx, cy, labelR, a.angle);
        const isGap = a.score < 3.0;
        return (
          <text
            key={`label-${a.slug}`}
            x={lp.x}
            y={lp.y}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={9.5}
            fontWeight={isGap ? 700 : 500}
            fill={isGap ? "#D8443C" : "#64748b"}
            fontFamily="'Montserrat', system-ui, sans-serif"
          >
            {a.name}
          </text>
        );
      })}
    </svg>
  );
}

// ── Card ─────────────────────────────────────────────────────────────────────

type Props = {
  dimensions: CompetencyDimension[];
  sessionCount: number;
};

export function CompetencyProfileCard({ dimensions, sessionCount }: Props) {
  const average = useMemo(
    () => (dimensions.length ? dimensions.reduce((a, d) => a + d.score, 0) / dimensions.length : 0),
    [dimensions],
  );

  return (
    <div
      style={{
        background: "#fff",
        borderRadius: 16,
        border: "1px solid #e2e8f0",
        padding: "20px 22px 18px",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 8,
        }}
      >
        <div>
          <p
            style={{
              fontSize: 10,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.12em",
              color: "#407EC1",
              margin: "0 0 3px",
              fontFamily: "'Montserrat', sans-serif",
            }}
          >
            Progress
          </p>
          <h3
            style={{
              fontFamily: "'Montserrat', sans-serif",
              fontWeight: 800,
              fontSize: 15,
              color: "#001847",
              margin: 0,
            }}
          >
            Competency profile
          </h3>
          <p style={{ fontSize: 11, color: "#94a3b8", margin: "3px 0 0" }}>
            {sessionCount} session{sessionCount !== 1 ? "s" : ""} · last 20 scored
          </p>
        </div>
        <div style={{ textAlign: "right" }}>
          <div
            style={{
              fontFamily: "'Montserrat', sans-serif",
              fontWeight: 800,
              fontSize: 30,
              color: "#001847",
              lineHeight: 1,
              letterSpacing: "-0.02em",
            }}
          >
            {average.toFixed(1)}
          </div>
          <div
            style={{
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: "0.12em",
              color: "#94a3b8",
              marginTop: 3,
            }}
          >
            AVG / {SCALE}.0
          </div>
        </div>
      </div>

      {/* Radar chart */}
      <div style={{ padding: "4px 0 8px" }}>
        <RadarChart dimensions={dimensions} size={220} />
      </div>

      {/* Score bars */}
      <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
        {dimensions.map((d) => {
          const pct = Math.max(0, Math.min(1, d.score / SCALE)) * 100;
          const color = scoreColor(d.score);
          const display = Number.isInteger(d.score) ? String(d.score) : d.score.toFixed(1);
          return (
            <div key={d.slug}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "baseline",
                  marginBottom: 4,
                }}
              >
                <span style={{ fontSize: 12, color: "#374151", fontWeight: 500 }}>{d.name}</span>
                <span
                  style={{
                    fontSize: 12,
                    color: "#001847",
                    fontWeight: 700,
                    fontFamily: "'Montserrat', sans-serif",
                  }}
                >
                  {display}
                </span>
              </div>
              <div
                style={{ height: 5, borderRadius: 999, background: "#f0f4fb", overflow: "hidden" }}
                role="progressbar"
                aria-label={`${d.name}: ${display} out of ${SCALE}`}
                aria-valuenow={d.score}
                aria-valuemin={0}
                aria-valuemax={SCALE}
              >
                <div
                  style={{
                    height: "100%",
                    borderRadius: 999,
                    background: color,
                    width: `${pct}%`,
                    transition: "width 0.6s ease",
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
